import hsl2rgb from '../hsl/hsl2rgb.js';
import lab2rgb from '../lab/lab2rgb.js';
import lch2rgb from '../lch/lch2rgb.js';
import oklab2rgb from '../oklab/oklab2rgb.js';
import oklch2rgb from '../oklch/oklch2rgb.js';
import input from '../input.js';
import limit from '../../utils/limit.js';
import { getLabWhitePoint, setLabWhitePoint } from '../lab/lab-constants.js';
var INT_OR_PCT = /((?:-?\d+)|(?:-?\d+(?:\.\d+)?)%|none)/.source;
var FLOAT_OR_PCT = /((?:-?(?:\d+(?:\.\d*)?|\.\d+)%?)|none)/.source;
var PCT = /((?:-?(?:\d+(?:\.\d*)?|\.\d+)%)|none)/.source;
var RE_S = /\s*/.source;
var SEP = /\s+/.source;
var COMMA = /\s*,\s*/.source;
var ANLGE = /((?:-?(?:\d+(?:\.\d*)?|\.\d+)(?:deg)?)|none)/.source;
var ALPHA = /\s*(?:\/\s*((?:[01]|[01]?\.\d+)|\d+(?:\.\d+)?%))?/.source;

// e.g. rgb(250 20 0), rgb(100% 50% 20%), rgb(100% 50% 20% / 0.5)
var RE_RGB = new RegExp('^rgba?\\(' + RE_S + [INT_OR_PCT, INT_OR_PCT, INT_OR_PCT].join(SEP) + ALPHA + '\\)$');
var RE_RGB_LEGACY = new RegExp('^rgb\\(' + RE_S + [INT_OR_PCT, INT_OR_PCT, INT_OR_PCT].join(COMMA) + RE_S + '\\)$');
var RE_RGBA_LEGACY = new RegExp('^rgba\\(' + RE_S + [INT_OR_PCT, INT_OR_PCT, INT_OR_PCT, FLOAT_OR_PCT].join(COMMA) + RE_S + '\\)$');
var RE_HSL = new RegExp('^hsla?\\(' + RE_S + [ANLGE, PCT, PCT].join(SEP) + ALPHA + '\\)$');
var RE_HSL_LEGACY = new RegExp('^hsl?\\(' + RE_S + [ANLGE, PCT, PCT].join(COMMA) + RE_S + '\\)$');
var RE_HSLA_LEGACY = /^hsla\(\s*(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)%\s*,\s*(-?\d+(?:\.\d+)?)%\s*,\s*([01]|[01]?\.\d+)\)$/;
var RE_LAB = new RegExp('^lab\\(' + RE_S + [FLOAT_OR_PCT, FLOAT_OR_PCT, FLOAT_OR_PCT].join(SEP) + ALPHA + '\\)$');
var RE_LCH = new RegExp('^lch\\(' + RE_S + [FLOAT_OR_PCT, FLOAT_OR_PCT, ANLGE].join(SEP) + ALPHA + '\\)$');
var RE_OKLAB = new RegExp('^oklab\\(' + RE_S + [FLOAT_OR_PCT, FLOAT_OR_PCT, FLOAT_OR_PCT].join(SEP) + ALPHA + '\\)$');
var RE_OKLCH = new RegExp('^oklch\\(' + RE_S + [FLOAT_OR_PCT, FLOAT_OR_PCT, ANLGE].join(SEP) + ALPHA + '\\)$');
var round = Math.round;
var roundRGB = function roundRGB(rgb) {
  return rgb.map(function (v, i) {
    return i <= 2 ? limit(round(v), 0, 255) : v;
  });
};
var percentToAbsolute = function percentToAbsolute(pct) {
  var min = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var max = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 100;
  var signed = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  if (typeof pct === 'string' && pct.endsWith('%')) {
    pct = parseFloat(pct.substring(0, pct.length - 1)) / 100;
    if (signed) {
      // signed percentages are in the range -100% to 100%
      pct = min + (pct + 1) * 0.5 * (max - min);
    } else {
      pct = min + pct * (max - min);
    }
  }
  return +pct;
};
var noneToValue = function noneToValue(v, noneValue) {
  return v === 'none' ? noneValue : v;
};
var css2rgb = function css2rgb(css) {
  css = css.toLowerCase().trim();
  if (css === 'transparent') {
    return [0, 0, 0, 0];
  }
  var m;
  if (input.format.named) {
    try {
      return input.format.named(css);
      // eslint-disable-next-line
    } catch (e) {}
  }

  // rgb(250 20 0) or rgb(250,20,0)
  if ((m = css.match(RE_RGB)) || (m = css.match(RE_RGB_LEGACY))) {
    var rgb = m.slice(1, 4);
    for (var i = 0; i < 3; i++) {
      rgb[i] = +percentToAbsolute(noneToValue(rgb[i], 0), 0, 255);
    }
    rgb = roundRGB(rgb);
    var alpha = m[4] !== undefined ? +percentToAbsolute(m[4], 0, 1) : 1;
    rgb[3] = alpha; // default alpha
    return rgb;
  }

  // rgba(250,20,0,0.4)
  if (m = css.match(RE_RGBA_LEGACY)) {
    var _rgb = m.slice(1, 5);
    for (var _i = 0; _i < 4; _i++) {
      _rgb[_i] = +percentToAbsolute(_rgb[_i], 0, 255);
    }
    return _rgb;
  }

  // hsl(0,100%,50%)
  if ((m = css.match(RE_HSL)) || (m = css.match(RE_HSL_LEGACY))) {
    var hsl = m.slice(1, 4);
    hsl[0] = +noneToValue(hsl[0].replace('deg', ''), 0);
    hsl[1] = +percentToAbsolute(noneToValue(hsl[1], 0), 0, 100) * 0.01;
    hsl[2] = +percentToAbsolute(noneToValue(hsl[2], 0), 0, 100) * 0.01;
    var _rgb2 = roundRGB(hsl2rgb(hsl));
    var _alpha = m[4] !== undefined ? +percentToAbsolute(m[4], 0, 1) : 1;
    _rgb2[3] = _alpha;
    return _rgb2;
  }

  // hsla(0,100%,50%,0.5)
  if (m = css.match(RE_HSLA_LEGACY)) {
    var _hsl = m.slice(1, 4);
    _hsl[1] *= 0.01;
    _hsl[2] *= 0.01;
    var _rgb3 = hsl2rgb(_hsl);
    for (var _i2 = 0; _i2 < 3; _i2++) {
      _rgb3[_i2] = round(_rgb3[_i2]);
    }
    _rgb3[3] = +m[4]; // default alpha = 1
    return _rgb3;
  }
  if (m = css.match(RE_LAB)) {
    var lab = m.slice(1, 4);
    lab[0] = percentToAbsolute(noneToValue(lab[0], 0), 0, 100);
    lab[1] = percentToAbsolute(noneToValue(lab[1], 0), -125, 125, true);
    lab[2] = percentToAbsolute(noneToValue(lab[2], 0), -125, 125, true);
    // convert to D50 Lab whitepoint
    var wp = getLabWhitePoint();
    setLabWhitePoint('d50');
    var _rgb4 = roundRGB(lab2rgb(lab));
    // convert back to original Lab whitepoint
    setLabWhitePoint(wp);
    var _alpha2 = m[4] !== undefined ? +percentToAbsolute(m[4], 0, 1) : 1;
    _rgb4[3] = _alpha2;
    return _rgb4;
  }
  if (m = css.match(RE_LCH)) {
    var lch = m.slice(1, 4);
    lch[0] = percentToAbsolute(lch[0], 0, 100);
    lch[1] = percentToAbsolute(noneToValue(lch[1], 0), 0, 150, false);
    lch[2] = +noneToValue(lch[2].replace('deg', ''), 0);
    // convert to D50 Lab whitepoint
    var _wp = getLabWhitePoint();
    setLabWhitePoint('d50');
    var _rgb5 = roundRGB(lch2rgb(lch));
    // convert back to original Lab whitepoint
    setLabWhitePoint(_wp);
    var _alpha3 = m[4] !== undefined ? +percentToAbsolute(m[4], 0, 1) : 1;
    _rgb5[3] = _alpha3;
    return _rgb5;
  }
  if (m = css.match(RE_OKLAB)) {
    var oklab = m.slice(1, 4);
    oklab[0] = percentToAbsolute(noneToValue(oklab[0], 0), 0, 1);
    oklab[1] = percentToAbsolute(noneToValue(oklab[1], 0), -0.4, 0.4, true);
    oklab[2] = percentToAbsolute(noneToValue(oklab[2], 0), -0.4, 0.4, true);
    var _rgb6 = roundRGB(oklab2rgb(oklab));
    var _alpha4 = m[4] !== undefined ? +percentToAbsolute(m[4], 0, 1) : 1;
    _rgb6[3] = _alpha4;
    return _rgb6;
  }
  if (m = css.match(RE_OKLCH)) {
    var oklch = m.slice(1, 4);
    oklch[0] = percentToAbsolute(noneToValue(oklch[0], 0), 0, 1);
    oklch[1] = percentToAbsolute(noneToValue(oklch[1], 0), 0, 0.4, false);
    oklch[2] = +noneToValue(oklch[2].replace('deg', ''), 0);
    var _rgb7 = roundRGB(oklch2rgb(oklch));
    var _alpha5 = m[4] !== undefined ? +percentToAbsolute(m[4], 0, 1) : 1;
    _rgb7[3] = _alpha5;
    return _rgb7;
  }
};
css2rgb.test = function (s) {
  return (
    // modern
    RE_RGB.test(s) || RE_HSL.test(s) || RE_LAB.test(s) || RE_LCH.test(s) || RE_OKLAB.test(s) || RE_OKLCH.test(s) ||
    // legacy
    RE_RGB_LEGACY.test(s) || RE_RGBA_LEGACY.test(s) || RE_HSL_LEGACY.test(s) || RE_HSLA_LEGACY.test(s) || s === 'transparent'
  );
};
export default css2rgb;